/* ============================================================
   CHRONCES — <cr-to-top>
   Flèche « remonter en haut ».

   Elle n'existe que si la page vaut la peine d'être remontée :
   sous le seuil, le bouton reste `hidden` et n'occupe rien.
   ============================================================ */

class CrToTop extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;

    this.threshold = (parseFloat(this.dataset.showAfter) || 1.5) * window.innerHeight;

    this.button.addEventListener('click', this.onClick);
    // Passif : on ne fait que lire la position, jamais l'annuler.
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    this.sync();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.threshold = (parseFloat(this.dataset.showAfter) || 1.5) * window.innerHeight;
    this.sync();
  };

  // Le défilement se déclenche à chaque pixel : on ne touche au DOM que
  // lorsque l'état CHANGE, sinon on écrit des centaines de fois la même
  // valeur pendant une descente.
  onScroll = () => {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.sync();
    });
  };

  sync() {
    const shouldShow = window.scrollY > this.threshold;
    if (shouldShow === this.shown) return;
    this.shown = shouldShow;
    this.button.toggleAttribute('hidden', !shouldShow);
  }

  onClick = () => {
    const selector = this.dataset.target;
    let top = 0;

    if (selector) {
      const target = document.querySelector(selector);
      if (target) {
        // L'en-tête est collant : viser le haut brut de la section la
        // ferait passer dessous. `--cr-header-height` est publiée sur
        // <html> par cr-header.js (§12c).
        const raw = getComputedStyle(document.documentElement).getPropertyValue('--cr-header-height');
        const header = parseFloat(raw) || 0;
        top = window.scrollY + target.getBoundingClientRect().top - header - 12;
        if (top < 0) top = 0;
      }
    }

    // `prefers-reduced-motion` : un défilement animé de 15 000 px est
    // exactement le genre de mouvement que ce réglage demande d'éviter.
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top, behavior: calm ? 'auto' : 'smooth' });

    // Le focus doit suivre le regard, sinon un utilisateur au clavier
    // reste en bas de page après avoir remonté l'affichage.
    const anchor = document.querySelector('#MainContent, main, body');
    if (anchor) {
      anchor.setAttribute('tabindex', '-1');
      anchor.focus({ preventScroll: true });
      anchor.removeAttribute('tabindex');
    }
  };
}

if (!customElements.get('cr-to-top')) {
  customElements.define('cr-to-top', CrToTop);
}
